import React from 'react';
import { Card, CardMedia } from '@withjoy/joykit/components/Card';
import { CardBlockText, CardContent, CardCta, CardEyebrow, CardGradient, CardGrid, CardHed, CardSafeContentArea, styles } from './CurationCard.styles';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCurationCardGrid } from './useCurationCard';
import type { CurationCardGridProps, CurationCardProps } from './CurationCard.types';
import { Skeleton, SkeletonGroup } from '@shared/components/Skeleton';
import { range } from 'lodash-es';
import { useTranslation } from '@shared/core';

const PLACEHOLDER_RANGE = range(0, 6);
const CARD_HEIGHT = ['278px', null, '480px'];

export const CurationCard = (props: CurationCardProps) => {
  const curationData = props.curation.curationData?.header || {};
  const { backgroundImage, title, eyebrow } = curationData;
  const { t } = useTranslation('catalogRegistry');
  const { explore } = t('curationsSection');

  const textColor = curationData.textColor || undefined;
  const gradientColor = curationData.gradientColor || undefined;

  const showBlockText = !!(eyebrow || title);

  const handleOnClick = useEventCallback<React.MouseEventHandler>(e => {
    props.onClick?.(props.curation, props.index, e);
  });

  return (
    <Card
      overrides={{ Root: { props: { width: ['unset'], height: CARD_HEIGHT } } }}
      onClick={props.onClick ? handleOnClick : undefined}
    >
      {backgroundImage && (
        <CardMedia
          enableWindowPeek={true}
          windowHeight="120px"
          src={backgroundImage.url}
        />
      )}

      <CardGradient __css={styles.gradient} />

      <CardSafeContentArea __css={styles.safeContentArea}>
        <CardContent
          __css={styles.content}
          color={textColor}
        >
          {showBlockText && (
            <CardBlockText __css={styles.blockText}>
              {eyebrow ? <CardEyebrow __css={styles.eyebrow}>{eyebrow}</CardEyebrow> : null}

              {title && (
                <CardHed
                  as="h1"
                  __css={styles.hed}
                >
                  {title}
                </CardHed>
              )}

              <CardCta
                as="button"
                __css={styles.cta}
                backgroundColor={textColor}
                color={gradientColor}
              >
                {explore()}
              </CardCta>
            </CardBlockText>
          )}
        </CardContent>
      </CardSafeContentArea>
    </Card>
  );
};

CurationCard.Skeleton = () => {
  return (
    <Skeleton
      height={CARD_HEIGHT}
      borderRadius="8px"
    />
  );
};

const Grid: React.FC = ({ children }) => {
  return <CardGrid __css={styles.grid}>{children}</CardGrid>;
};

export const CurationCardGrid = (props: CurationCardGridProps) => {
  const { curations, showSkeleton } = props;
  const { onCurationClick } = useCurationCardGrid(props);
  return (
    <SkeletonGroup
      isReady={!showSkeleton}
      width={[null, null, '80%']}
      maxWidth={['100%', null, '1549px']}
      marginBottom="80px"
      placeholder={
        <Grid>
          {PLACEHOLDER_RANGE.map((x, idx) => (
            <CurationCard.Skeleton key={idx} />
          ))}
        </Grid>
      }
    >
      <Grid>
        {curations.map((x, idx) => (
          <CurationCard
            key={x.id}
            index={idx}
            curation={x}
            onClick={onCurationClick}
          />
        ))}
      </Grid>
    </SkeletonGroup>
  );
};
